import { useCallback, useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { NavLink } from 'react-router-dom';
import { Star, X } from '../../icons';
import { Badge } from '../shared/Badge';
import { useAllMenuStore } from '../../store/allMenuStore';
import { useModalLeave } from '../../hooks/useModalLeave';
import { useEscapeClose } from '../../hooks/useEscapeClose';
import { tLayout, getLayoutLang, subscribeLayoutLang } from '../../lib/layout-i18n';
import { useAuthStore } from '../../store/authStore';
import { useFavoritesStore, FAVORITES_MAX } from '../../store/favoritesStore';
import { RAIL_MENU_PIN_ENABLED } from '../../lib/layout-shell';
import type { AllMenuEntry } from './AllMenuPanel';
import './AllMenuPopover.css';

/**
 * 전체메뉴 **팝오버** — 2026-08-17 사용자 지시.
 *
 *   "전체메뉴를 클릭하면 프로필처럼 팝업이 나타나고 목록이 보여야 한다"
 *
 * 종전 `AllMenuPanel` 은 좌측 레일 옆에서 밀려나오는 460px 드로어 + 화면을 덮는 스크림이었다.
 * 같은 앱 안에서 ⑤ 레일 프로필은 앵커에 붙는 작은 팝오버인데 ① 레일 전체메뉴만 드로어라
 * 두 곳의 규칙이 달랐다. 이 컴포넌트는 **프로필 팝오버(ProfilePopover)와 같은 계약**을 쓴다:
 *   · body 로 portal + 앵커 실측(getBoundingClientRect) 기준 fixed 배치
 *   · 스크림 없음 — 바깥 클릭 · ESC 로 닫기
 *   · `useModalLeave(160)` 진입·퇴장 모션
 * 다른 점은 **방향**뿐이다. 프로필은 우측 레일이라 왼쪽으로 열리고, 전체메뉴는 좌측 레일이라
 * 오른쪽으로 열린다. 항목이 레일 하단에 있으므로 세로는 **버튼 아래끝에 맞춰** 위로 자란다.
 *
 * ⚠ 목록은 여기서도 **NavBar 가 계산한 것을 주입받는다**(자체 fetch 없음) — `AllMenuPanel` 머리말과
 * 같은 이유다. /menu-config(admin 메뉴 레지스트리)를 두 곳에서 각자 읽으면 관리자가 메뉴를 끈 뒤
 * 사이드바와 전체메뉴가 갈라진다. 의도적 비노출 메뉴(급여·인사·전자결재)도 NavBar 가 안 주니
 * 자동으로 여기서 빠진다.
 *
 * 표현은 **카드 격자가 아니라 목록(행)** 이다 — 팝오버 폭(288px)에서는 카드가 두 칸밖에 안 들어가
 * 세로만 길어지고, 사용자 요구도 "목록이 보여야 한다" 였다.
 *
 * 롤백: `VITE_ALLMENU_POPOVER='0'` → NavBar 가 종전 `AllMenuPanel` 을 그린다(layout-shell.ts).
 */

interface Props {
  primary: AllMenuEntry[];
  secondary: AllMenuEntry[];
  /**
   * 보조영역(관리 콘솔 "메뉴 관리 > 위치 = 보조영역", 2026-08-19). 좌측 레일에는 걸리지 않고
   * **여기에서만** 보이는 메뉴들이다 — 별도 그룹으로 구분해 "왜 사이드바에 없지" 를 없앤다.
   * 선택(optional) 이라 종전 호출부는 그대로 동작한다(회귀 0).
   */
  aux?: AllMenuEntry[];
  /** 앵커(레일 최하단 "전체메뉴" 버튼). 팝오버는 이 요소의 오른쪽에 붙는다. */
  anchorRef: React.RefObject<HTMLElement>;
}

/** 팝오버 폭·여백(px) — CSS `.allmenu-pop` 과 동기 유지. */
const POP_WIDTH = 288;
const GAP = 10;
const EDGE = 12;

export function AllMenuPopover({ primary, secondary, aux, anchorRef }: Props): JSX.Element | null {
  useSyncExternalStore(subscribeLayoutLang, getLayoutLang, getLayoutLang);
  const open = useAllMenuStore((s) => s.open);
  const setOpen = useAllMenuStore((s) => s.setOpen);

  /**
   * 별(★) 고정 목록은 사용자별 저장값이다 — 레일(RightRailHost)이 이미 읽어 오지만, 그쪽이
   * 없는 화면·좁은 폭에서도 별이 옳은 상태로 보여야 하므로 여기서도 확인한다.
   * `hydrate` 는 같은 사용자면 no-op 이라 중복 호출이 서로를 덮지 않는다.
   */
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const hydrateFavorites = useFavoritesStore((s) => s.hydrate);
  useEffect(() => { hydrateFavorites(userId); }, [userId, hydrateFavorites]);

  const panelRef = useRef<HTMLDivElement | null>(null);
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const restoreRef = useRef<HTMLElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const { shouldRender, isLeaving } = useModalLeave(open, 160);

  const close = useCallback(() => setOpen(false), [setOpen]);
  useEscapeClose(close, !open);

  // 앵커 기준 위치 — 열리는 순간 + 리사이즈/스크롤 시 재계산(ProfilePopover 와 같은 방식).
  const place = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const h = panelRef.current?.offsetHeight ?? 360;
    // 가로: 레일 오른쪽에 붙인다. 좁은 화면에서 오른쪽으로 넘치면 화면 안으로 당긴다.
    const left = Math.min(r.right + GAP, Math.max(EDGE, window.innerWidth - POP_WIDTH - EDGE));
    // 세로: 버튼 아래끝 정렬(= 버튼에서 솟는 모양). 위/아래로 넘치면 화면 안으로 당긴다.
    const top = Math.min(Math.max(EDGE, r.bottom - h), Math.max(EDGE, window.innerHeight - h - EDGE));
    setPos({ top, left });
  }, [anchorRef]);

  useLayoutEffect(() => { if (shouldRender) place(); }, [shouldRender, primary, secondary, place]);

  useEffect(() => {
    if (!open) return;
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, place]);

  // 바깥 클릭 닫기 — 앵커는 제외한다(앵커 클릭은 토글이라 여기서 닫으면 곧바로 다시 열려 깜빡인다).
  useEffect(() => {
    if (!open) return;
    const onPointer = (e: MouseEvent) => {
      const t = e.target as Node;
      if (panelRef.current?.contains(t)) return;
      if (anchorRef.current?.contains(t)) return;
      close();
    };
    const timer = setTimeout(() => document.addEventListener('mousedown', onPointer), 0);
    return () => { clearTimeout(timer); document.removeEventListener('mousedown', onPointer); };
  }, [open, close, anchorRef]);

  // 포커스 — 열 때 닫기 버튼으로, 닫을 때 직전 요소(=전체메뉴 버튼)로 복원(종전 패널과 같은 계약).
  useEffect(() => {
    if (open) {
      restoreRef.current = document.activeElement as HTMLElement | null;
      closeRef.current?.focus();
      return;
    }
    const prev = restoreRef.current;
    restoreRef.current = null;
    if (prev && document.contains(prev)) prev.focus();
  }, [open]);

  const auxItems = aux ?? [];
  const groups = [
    { key: 'work', title: tLayout('layout.allmenu.group_work'), items: primary },
    { key: 'org', title: tLayout('layout.allmenu.group_org'), items: secondary },
    // 보조영역 — 레일에 없는 메뉴라 맨 아래 따로 둔다(빈 그룹은 아래 filter 가 걷어낸다).
    { key: 'aux', title: tLayout('layout.allmenu.group_aux'), items: auxItems },
  ].filter((g) => g.items.length > 0);

  const total = primary.length + secondary.length + auxItems.length;
  const closeLabel = tLayout('layout.allmenu.close');

  if (!shouldRender) return null;

  return createPortal(
    <div
      ref={panelRef}
      className={`allmenu-pop${isLeaving ? ' allmenu-pop--leaving' : ''}`}
      style={pos ? { top: pos.top, left: pos.left } : { visibility: 'hidden' }}
      role="dialog"
      aria-label={tLayout('layout.nav.all_menu')}
      aria-hidden={isLeaving || undefined}
    >
      <div className="allmenu-pop__head">
        <b className="allmenu-pop__title">{tLayout('layout.nav.all_menu')}</b>
        <span className="allmenu-pop__count">{tLayout('layout.allmenu.count', { n: total })}</span>
        <button
          type="button"
          ref={closeRef}
          className="allmenu-pop__close"
          aria-label={closeLabel}
          title={closeLabel}
          onClick={close}
        >
          <X size={16} />
        </button>
      </div>

      <div className="allmenu-pop__body scrollbar-thin">
        {groups.map((g) => (
          <div key={g.key} className="allmenu-pop__group">
            <div className="allmenu-pop__grouptitle">{g.title}</div>
            <div className="allmenu-pop__list">
              {g.items.map((item) => (
                <AllMenuRow key={item.to} item={item} onPick={close} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>,
    document.body,
  );
}

/** 목록 한 행 — 아이콘 칩 + 전체 라벨 + 배지. 프로필 팝오버의 행(profile-pop__row)과 같은 리듬. */
function AllMenuRow({ item, onPick }: { item: AllMenuEntry; onPick: () => void }) {
  const Icon = item.icon;
  const inner = (
    <>
      <span className="allmenu-pop__rowico" aria-hidden>
        {item.svg
          ? <span className="allmenu-pop__svg" dangerouslySetInnerHTML={{ __html: item.svg }} />
          : <Icon size={16} />}
      </span>
      <span className="allmenu-pop__rowlabel">{item.labelFull ?? item.label}</span>
      {item.badge !== undefined && item.badge > 0 && (
        <Badge variant="count" size="sm">{item.badge > 99 ? '99+' : item.badge}</Badge>
      )}
    </>
  );
  const link = item.external ? (
    <a className="allmenu-pop__row" href={item.to} target="_blank" rel="noopener noreferrer" onClick={onPick}>
      {inner}
    </a>
  ) : (
    <NavLink
      to={item.to}
      className={({ isActive }) => ['allmenu-pop__row', isActive && 'is-active'].filter(Boolean).join(' ')}
      onClick={onPick}
    >
      {inner}
    </NavLink>
  );

  // 롤백(`VITE_RAIL_MENU_PIN='0'`) 이면 별을 달지 않는다 — 마크업이 종전과 완전히 같다.
  if (!RAIL_MENU_PIN_ENABLED) return link;

  /**
   * 별(★)은 링크의 **형제**다 — `<button>` 을 `<a>` 안에 넣을 수 없기 때문(대화형 요소 중첩).
   * 감싸는 `div` 는 위치 기준만 잡고, 링크 자체(`.allmenu-pop__row`)는 한 줄도 바뀌지 않는다.
   */
  return (
    <div className="allmenu-pop__rowwrap">
      {link}
      <AllMenuPinStar item={item} />
    </div>
  );
}

/**
 * 메뉴명 뒤의 별(★) — 2026-08-19 사용자 지시.
 *
 *   "전체메뉴에서 메뉴명에 마우스를 올리면 메뉴명 뒤에 별모양이 생기고 그걸 클릭하면
 *    우측 레일에 해당 메뉴 표시되도록 한다."
 *
 * 눌린 것(고정됨)은 **항상 보이고**(채운 별), 안 눌린 것은 호버·포커스에서만 나타난다 — 목록을
 * 훑을 때 별이 줄줄이 서서 라벨을 방해하지 않게. 저장은 `favoritesStore`(사용자별 localStorage)
 * 를 그대로 쓴다: 담는 것이 종전과 같은 "레일에 세울 메뉴 포인터" 라 새 저장소를 만들 이유가 없고,
 * 그래야 기존에 등록해 둔 것이 그대로 이어진다.
 */
function AllMenuPinStar({ item }: { item: AllMenuEntry }) {
  const items = useFavoritesStore((s) => s.items);
  const add = useFavoritesStore((s) => s.add);
  const remove = useFavoritesStore((s) => s.remove);

  const label = item.labelFull ?? item.label;
  const pinned = items.some((f) => f.to === item.to);
  // 상한에 닿았는데 아직 고정 안 된 항목 — 눌리는 척하지 않고 사유를 말해 준다.
  const full = !pinned && items.length >= FAVORITES_MAX;
  const title = full
    ? tLayout('layout.allmenu.pin_full', { n: FAVORITES_MAX })
    : tLayout(pinned ? 'layout.allmenu.pin_remove' : 'layout.allmenu.pin_add', { name: label });

  return (
    <button
      type="button"
      className={`allmenu-pop__star${pinned ? ' is-on' : ''}`}
      aria-pressed={pinned}
      disabled={full}
      title={title}
      aria-label={title}
      onClick={(e) => {
        // 행(링크) 위에 겹쳐 있는 버튼이다 — 이동·팝오버 닫기로 번지면 안 된다.
        e.preventDefault();
        e.stopPropagation();
        if (pinned) remove(item.to);
        else add({ to: item.to, label, external: item.external });
      }}
    >
      <Star size={14} aria-hidden {...(pinned ? { fill: 'currentColor' } : {})} />
    </button>
  );
}
